import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, BookOpen, User } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useOrgBatches, useOrgStudents } from '@/lib/hooks/useOrg';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';

type BatchItem = { kind: 'batch'; id: string; name: string };
type StudentItem = { kind: 'student'; batchId: string; userId: string; name: string; email: string; batchName: string };
type ResultItem = BatchItem | StudentItem;

export function OrgCommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: batches } = useOrgBatches();
  const { data: students } = useOrgStudents();

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const items = useMemo<ResultItem[]>(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    const b: BatchItem[] = (batches ?? [])
      .filter((x) => x.name.toLowerCase().includes(query))
      .slice(0, 5)
      .map((x) => ({ kind: 'batch', id: x.id, name: x.name }));
    const s: StudentItem[] = (students ?? [])
      .filter((x) => x.name.toLowerCase().includes(query) || x.email.toLowerCase().includes(query))
      .slice(0, 8)
      .map((x) => ({ kind: 'student', batchId: x.batch_id, userId: x.user_id, name: x.name, email: x.email, batchName: x.batch_name }));
    return [...b, ...s];
  }, [q, batches, students]);

  useEffect(() => { setCursor((c) => Math.min(c, Math.max(items.length - 1, 0))); }, [items.length]);

  const go = (item: ResultItem) => {
    if (item.kind === 'batch') navigate({ to: '/org/batches/$batchId', params: { batchId: item.id } });
    else navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId: item.batchId, userId: item.userId } });
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && items[cursor]) go(items[cursor]);
  };

  const batchCount = items.filter((i) => i.kind === 'batch').length;
  const noResults = q.trim().length > 0 && items.length === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className="flex w-full max-w-lg items-center gap-2.5 rounded-2xl border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/60"
      >
        <Search className="h-[15px] w-[15px] shrink-0" />
        <span className="flex-1 text-left">Search students, batches, trainers…</span>
        <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">⌘ K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[15vh]" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-popover shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setCursor(0); }}
                onKeyDown={onKeyDown}
                placeholder="Search students & batches…"
                className="w-full bg-transparent py-3 text-sm outline-none"
              />
            </div>

            {q.trim().length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Type a student or batch name…</p>
            )}
            {noResults && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">No results for &ldquo;{q}&rdquo;</p>
            )}

            {items.length > 0 && (
              <div className="max-h-80 overflow-y-auto py-2">
                {items.map((item, i) => (
                  <button
                    key={item.kind === 'batch' ? `b-${item.id}` : `s-${item.batchId}-${item.userId}`}
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(item)}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${cursor === i ? 'bg-muted' : 'hover:bg-muted/50'}`}
                  >
                    {i === 0 && batchCount > 0 && <span className="sr-only">Batches</span>}
                    {item.kind === 'batch' ? (
                      <>
                        <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="flex-1 truncate text-sm">{item.name}</span>
                        <span className="text-xs text-muted-foreground">Batch</span>
                      </>
                    ) : (
                      <>
                        <OrgAvatar name={item.name} size={26} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{item.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{item.email} · {item.batchName}</p>
                        </div>
                        <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
